import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ArrowUpRight, Sparkle, Target, ShieldCheck, ChatCircleText,
  Users, Trophy, Buildings, ChartLineUp, Compass,
  Check, Sun, Moon, Monitor, Translate, MagnifyingGlass, Scales, Envelope, Clock,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Wordmark } from "@/components/Brand";
import { useI18n } from "@/context/I18nContext";
import { useTheme } from "@/context/ThemeContext";

// -----------------------------------------------------------------------------
// Copy — all landing-page prose lives here. Benefit-focused, natural, premium.
// Every card answers: "How does this help me achieve my goal?"
// -----------------------------------------------------------------------------
const COPY = {
  tr: {
    nav: { signin: "Giriş", signup: "Ücretsiz Başla" },
    eyebrow: "YAPAY ZEKÂ İŞ GELİŞTİRME ÇALIŞANI",
    hero: {
      title_1: "Kişisel ihtiyaçlardan",
      title_hl: "iş fırsatlarına.",
      title_2: "",
      subtitle: {
        before: "Ne aradığını anlat, Next io uygun seçenekleri ",
        find: "bulsun, ",
        compare: "kıyaslasın ve ",
        strategy: "stratejini hazırlasın.",
      },
      process: [
        { icon: MagnifyingGlass, title: "BULSUN", desc: "İhtiyacına uygun şirketleri ve fırsatları bulsun." },
        { icon: Scales, title: "KIYASLASIN", desc: "Seçenekleri analiz etsin, artı ve eksilerini karşılaştırsın." },
        { icon: Target, title: "STRATEJİNİ HAZIRLASIN", desc: "En iyi yaklaşım stratejisini hazırlasın, adım adım yol haritası sunsun." },
        { icon: Envelope, title: "MESAJINI YAZSIN", desc: "Doğru mesajları yazsın, iletişim kanallarını belirlesin." },
        { icon: ChartLineUp, title: "YANITLARI ANALİZ ETSİN", desc: "Gelen yanıtları analiz etsin, sonuçlara göre stratejini güncellesin." },
      ],
      benefitsTitle: "KİŞİSEL KULLANICILAR VE KOBİ’LER İÇİN TASARLANDI",
      benefits: [
        { icon: Users, label: "Yeni müşteri bul" },
        { icon: Buildings, label: "Doğru bağlantıları keşfet" },
        { icon: ChartLineUp, label: "Satışlarını artır" },
        { icon: Clock, label: "Zaman kazan" },
      ],
      cta_primary: "Ücretsiz Başla",
      cta_secondary: "Nasıl Çalışır",
      trust: "Kayıt için kredi kartı gerekmez. İlk fırsatları dakikalar içinde görün.",
    },
    section1: {
      eyebrow: "1 — Ne Kazanacaksınız?",
      title: "İhtiyacınız olan seçenekleri ve fırsatları bulun.",
      lead:
        "Kişisel bir ihtiyaç ya da iş hedefi için ayrı araçlara ihtiyacınız yok. Next io ne aradığınıza uyum sağlar ve doğru seçenekleri önce size getirir.",
      cards: [
        {
          icon: Users,
          title: "Müşteri Kazanın",
          desc:
            "Ürününüze ve hizmet alanınıza gerçekten uyan işletmeleri bulun. Her müşteri adayına neden uyduğunu Next io açıklar; siz zamanınızı sadece potansiyeli yüksek olanlara ayırırsınız.",
        },
        {
          icon: Trophy,
          title: "Sponsor Bulun",
          desc:
            "Etkinliğiniz, projeniz veya markanızla anlamlı hedef kitle ve marka uyumu olan şirketleri keşfedin. Hangileriyle konuşmaya değer olduğunu kanıta dayalı olarak değerlendirin.",
        },
        {
          icon: Buildings,
          title: "Etkinlik Katılımcısı Bulun",
          desc:
            "Etkinliğinizden profesyonel olarak fayda görebilecek uygun işletme ve profesyonel profilleri keşfedin.",
        },
      ],
    },
    section2: {
      eyebrow: "2 — Next io Farkı Nedir?",
      title: "Fırsatları anlamlı konuşmalara dönüştürün.",
      lead:
        "Bir liste bulmak kolay. Zor olan; doğru kişiyi, doğru kanalı ve doğru sözcükleri seçmek. Next io her adımda kanıta dayanır ve son sözü asla sizden almaz.",
      cards: [
        {
          icon: ShieldCheck,
          title: "İletişim Noktası Analizi",
          desc:
            "Her fırsat için kamuya açık ve doğrulanabilir iletişim kanallarını inceleriz. Doğru kişi doğrulanabiliyorsa gösteririz; doğrulanamıyorsa bunu açıkça belirtiriz.",
        },
        {
          icon: Compass,
          title: "İletişim Stratejisi",
          desc:
            "Her fırsat için hangi kanalın gerçekten işe yarayacağını sıralarız: doğrudan e-posta mı, telefon mu, web formu mu, LinkedIn mi? Yalnızca doğrulanabilir kanallar önerilir.",
        },
        {
          icon: ChatCircleText,
          title: "Kişiselleştirilmiş İlk Temas",
          desc:
            "Marka tonunuza göre hazırlanmış, saygılı ve satış-baskısı olmayan ilk temas taslakları. Otomatik gönderim yok — ne zaman ve nasıl konuşacağınıza siz karar verirsiniz.",
        },
      ],
    },
    proof: {
      title: "Kontrol her zaman sizde.",
      lead:
        "Next io asla otomatik mesaj göndermez. Sadece hazırlar, gerekçelendirir, sıralar. Karar hep sizin.",
      bullets: [
        "Her AI önerisi bir gerekçeye dayanır ve açıkça “AI Önerisi” olarak etiketlenir.",
        "Kişilerin adları veya kişisel e-posta adresleri asla karar-verici olarak sunulmaz.",
        "Sadece kamuya açık, doğrulanabilir kanallar sıralamada yer alır.",
      ],
    },
    finalCta: {
      title: "Bugün doğru fırsatları görmeye başlayın.",
      lead: "Kayıt olun, neye ihtiyacınız olduğunu anlatın ve Next io'ın sizin için hazırladığı ilk seçenekleri görün.",
      primary: "Ücretsiz Başla",
      secondary: "Zaten hesabım var",
    },
    footer: {
      product: "Ürün",
      resources: "Kaynaklar",
      legal: "Yasal",
      copyright: "© 2026 Next io. Tüm hakları saklıdır.",
    },
  },
  en: {
    nav: { signin: "Sign in", signup: "Get Started" },
    eyebrow: "YAPAY ZEKÂ İŞ GELİŞTİRME ÇALIŞANI",
    hero: {
      title_1: "From personal needs to",
      title_hl: "business opportunities.",
      title_2: "",
      subtitle: {
        before: "Tell Next io what you need; it can ",
        find: "find the right options, ",
        compare: "compare them and ",
        strategy: "build your strategy.",
      },
      process: [
        { icon: MagnifyingGlass, title: "FIND", desc: "Find companies and opportunities that fit what you need." },
        { icon: Scales, title: "COMPARE", desc: "Analyze options and compare their strengths and trade-offs." },
        { icon: Target, title: "BUILD YOUR STRATEGY", desc: "Prepare the best approach with a clear step-by-step roadmap." },
        { icon: Envelope, title: "WRITE YOUR MESSAGE", desc: "Write the right messages and identify the best channels." },
        { icon: ChartLineUp, title: "ANALYZE REPLIES", desc: "Analyze responses and update your strategy with the results." },
      ],
      benefitsTitle: "DESIGNED FOR PERSONAL USERS AND SMALL BUSINESSES",
      benefits: [
        { icon: Users, label: "Find new customers" },
        { icon: Buildings, label: "Discover the right connections" },
        { icon: ChartLineUp, label: "Grow sales" },
        { icon: Clock, label: "Save time" },
      ],
      cta_primary: "Get Started",
      cta_secondary: "See how it works",
      trust: "No credit card required. See your first opportunities in minutes.",
    },
    section1: {
      eyebrow: "1 — What can you achieve?",
      title: "Find the right options and opportunities for your goals.",
      lead:
        "You shouldn't need a different tool for every personal need or business goal. Next io adapts to what you're trying to achieve and puts the right options in front of you first.",
      cards: [
        {
          icon: Users,
          title: "Win Customers",
          desc:
            "Discover businesses that genuinely match what you sell and where you operate. Next io explains why each match makes sense so you spend time only on the ones worth your energy.",
        },
        {
          icon: Trophy,
          title: "Find Sponsors",
          desc:
            "Uncover companies whose values and audience align with your event, project or brand. See likely fit and priority before you reach out, so pitches don't feel like guesses.",
        },
        {
          icon: Buildings,
          title: "Attract Participants",
          desc:
            "Identify the organizations and communities most likely to show up for what you're building. Filter by industry, location and intent to reach the audience that actually cares.",
        },
      ],
    },
    section2: {
      eyebrow: "2 — Why is Next io different?",
      title: "Turn opportunities into meaningful conversations.",
      lead:
        "Finding a list is easy. The hard part is choosing the right person, the right channel and the right words. Next io anchors every step in evidence and never takes the last call away from you.",
      cards: [
        {
          icon: ShieldCheck,
          title: "Decision-Maker Analysis",
          desc:
            "We identify the actual decision-maker behind each opportunity using publicly available evidence. When the evidence isn't there, we say so — clearly and honestly, never guessing.",
        },
        {
          icon: Compass,
          title: "Communication Strategy",
          desc:
            "For every opportunity we rank the channels that are actually likely to work — direct email, phone, contact form or LinkedIn — and only ever surface routes we can verify.",
        },
        {
          icon: ChatCircleText,
          title: "Personalized Outreach",
          desc:
            "First-contact drafts written in your brand voice — respectful, concise and free of sales pressure. Nothing is ever sent automatically; you choose when and how to speak.",
        },
      ],
    },
    proof: {
      title: "You stay in control.",
      lead:
        "Next io never sends messages on your behalf. It only prepares, explains and prioritizes. The decision is always yours.",
      bullets: [
        "Every AI recommendation is grounded in evidence and clearly labeled as an “AI Recommendation.”",
        "A person's name or personal email address is never presented as a decision-maker.",
        "Only publicly verifiable channels are ever included in a recommendation.",
      ],
    },
    finalCta: {
      title: "Start seeing the right opportunities today.",
      lead: "Sign up, tell us what you need, and see the first options Next io has prepared for you.",
      primary: "Get Started",
      secondary: "I already have an account",
    },
    footer: {
      product: "Product",
      resources: "Resources",
      legal: "Legal",
      copyright: "© 2026 Next io. All rights reserved.",
    },
  },
};

// -----------------------------------------------------------------------------
// Page
// -----------------------------------------------------------------------------
export default function Home() {
  const { locale, setLocale } = useI18n();
  const { theme, setTheme } = useTheme();
  const c = COPY[locale] || COPY.tr;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <TopNav c={c} locale={locale} setLocale={setLocale} theme={theme} setTheme={setTheme} />
      <Hero c={c} />
      <SectionAchieve c={c} />
      <SectionDifference c={c} />
      <ProofBand c={c} />
      <FinalCTA c={c} />
      <Footer c={c} locale={locale} />
    </div>
  );
}

// -----------------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------------
function TopNav({ c, locale, setLocale, theme, setTheme }) {
  return (
    <header className="sticky top-0 z-30 backdrop-blur bg-background/80 border-b border-border/60">
      <div className="max-w-6xl mx-auto px-3 sm:px-6 min-h-16 py-2 flex flex-wrap items-center justify-between gap-2">
        <Link to="/" className="group" data-testid="home-brand">
          <Wordmark size="sm" />
        </Link>

        <div className="w-full sm:w-auto flex flex-wrap items-center justify-end gap-1 sm:gap-1.5">
          <button
            onClick={() => setLocale(locale === "tr" ? "en" : "tr")}
            className="h-9 px-2.5 rounded-md text-xs font-semibold border border-border/60 hover:border-primary/50 hover:bg-primary/5 transition inline-flex items-center gap-1.5"
            data-testid="home-lang-toggle"
            aria-label="Toggle language"
          >
            <Translate size={14} />
            {locale === "tr" ? "EN" : "TR"}
          </button>
          <ThemeToggle theme={theme} setTheme={setTheme} />
          <Link to="/giris">
            <Button variant="ghost" size="sm" className="text-sm" data-testid="home-signin">
              {c.nav.signin}
            </Button>
          </Link>
          <Link to="/kayit">
            <Button size="sm" className="text-sm gap-1.5" data-testid="home-signup">
              {c.nav.signup}
              <ArrowRight size={14} />
            </Button>
          </Link>
        </div>
      </div>
    </header>
  );
}

function ThemeToggle({ theme, setTheme }) {
  const next = theme === "light" ? "dark" : theme === "dark" ? "system" : "light";
  const Icon = theme === "light" ? Sun : theme === "dark" ? Moon : Monitor;
  return (
    <button
      onClick={() => setTheme(next)}
      className="h-9 w-9 rounded-md grid place-items-center border border-border/60 hover:border-primary/50 hover:bg-primary/5 transition"
      aria-label="Toggle theme"
      data-testid="home-theme-toggle"
    >
      <Icon size={15} />
    </button>
  );
}

function Hero({ c }) {
  return (
    <section className="relative acas-grid" data-testid="home-hero">
      <div className="max-w-6xl mx-auto px-6 pt-12 md:pt-16 pb-16 md:pb-20 relative">
        <div className="max-w-6xl mx-auto text-center">
          <div className="acas-eyebrow inline-flex px-4 py-1 rounded-full bg-primary/[0.06] text-primary mb-4">{c.eyebrow}</div>
          <div className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-none mb-5" data-testid="hero-brand-name">
            Next <span className="text-primary">io</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.02]">
            {c.hero.title_1}{" "}
            <span className="text-primary relative">
              {c.hero.title_hl}
              <span className="absolute inset-x-0 -bottom-1 h-1 bg-primary/25 rounded-full" aria-hidden />
            </span>{" "}
            {c.hero.title_2}
          </h1>
          <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed" data-testid="hero-supporting-copy">
            {c.hero.subtitle.before}
            <span className="text-primary font-semibold">{c.hero.subtitle.find}</span>
            <span className="text-primary font-semibold">{c.hero.subtitle.compare}</span>
            <span className="text-primary font-semibold">{c.hero.subtitle.strategy}</span>
          </p>

          <div className="mt-9 relative" data-testid="hero-process-flow">
            <div className="hidden lg:block absolute top-7 left-[9%] right-[9%] h-px bg-primary/45" aria-hidden />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-7 lg:gap-3 relative">
              {c.hero.process.map((step, index) => {
                const Icon = step.icon;
                return (
                  <div key={step.title} data-testid={`hero-process-step-${index + 1}`} className="min-w-0 flex flex-col items-center text-center">
                    <div className="w-14 h-14 rounded-full border border-primary/20 bg-background text-primary shadow-[0_8px_22px_-14px_rgba(37,99,235,0.7)] grid place-items-center relative z-10">
                      <Icon size={26} weight="duotone" />
                    </div>
                    <div className="mt-4 flex items-center gap-2 justify-center">
                      <span className="w-5 h-5 rounded-full bg-primary text-primary-foreground grid place-items-center text-[10px] font-bold">{index + 1}</span>
                      <span className="text-[11px] font-bold tracking-wide leading-tight">{step.title}</span>
                    </div>
                    <p className="mt-3 text-xs text-muted-foreground leading-relaxed max-w-[190px]">{step.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mt-9 flex flex-wrap gap-3 items-center justify-center">
            <Link to="/kayit">
              <Button size="lg" className="gap-2 h-11 px-5 text-sm" data-testid="hero-cta-primary">
                {c.hero.cta_primary}
                <ArrowRight size={16} weight="bold" />
              </Button>
            </Link>
            <a href="#achieve" className="text-sm font-semibold text-muted-foreground hover:text-foreground transition inline-flex items-center gap-1.5" data-testid="hero-cta-secondary">
              {c.hero.cta_secondary}
              <ArrowUpRight size={14} />
            </a>
          </div>

          <div className="mt-5 text-xs text-muted-foreground flex items-center justify-center gap-2">
            <Sparkle size={12} weight="fill" className="text-primary" />
            {c.hero.trust}
          </div>

          <div className="mt-12 max-w-4xl mx-auto" data-testid="hero-benefit-strip">
            <div className="flex items-center gap-3 text-[10px] sm:text-xs font-semibold tracking-wide text-muted-foreground">
              <span className="h-px flex-1 bg-border/80" />
              <span>{c.hero.benefitsTitle}</span>
              <span className="h-px flex-1 bg-border/80" />
            </div>
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
              {c.hero.benefits.map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <div key={benefit.label} data-testid={`hero-benefit-${index + 1}`} className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                    <Icon size={18} weight="duotone" className="text-primary flex-shrink-0" />
                    <span>{benefit.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function SectionEyebrow({ children }) {
  return (
    <div className="acas-eyebrow inline-flex items-center gap-2 mb-3">
      <span className="w-6 h-px bg-primary" />
      {children}
    </div>
  );
}

function SectionAchieve({ c }) {
  const s = c.section1;
  return (
    <section id="achieve" className="border-t border-border/60 bg-muted/[0.15]" data-testid="section-achieve">
      <div className="max-w-6xl mx-auto px-6 py-20 md:py-24">
        <div className="max-w-2xl">
          <SectionEyebrow>{s.eyebrow}</SectionEyebrow>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight leading-tight">
            {s.title}
          </h2>
          <p className="mt-5 text-base text-muted-foreground leading-relaxed">
            {s.lead}
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5">
          {s.cards.map((card, i) => (
            <OpportunityCard key={i} card={card} testId={`achieve-card-${i}`} />
          ))}
        </div>
      </div>
    </section>
  );
}

function OpportunityCard({ card, testId }) {
  const Icon = card.icon;
  return (
    <div
      data-testid={testId}
      className="group relative rounded-xl border border-border/60 bg-card p-6 hover:border-primary/40 hover:shadow-[0_0_0_1px_rgba(99,102,241,0.08),0_18px_40px_-24px_rgba(99,102,241,0.35)] transition"
    >
      <div className="w-11 h-11 rounded-lg bg-primary/10 text-primary grid place-items-center mb-4 group-hover:bg-primary/15 transition">
        <Icon size={20} weight="duotone" />
      </div>
      <div className="text-lg font-bold tracking-tight">{card.title}</div>
      <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{card.desc}</p>
    </div>
  );
}

function SectionDifference({ c }) {
  const s = c.section2;
  return (
    <section className="border-t border-border/60" data-testid="section-difference">
      <div className="max-w-6xl mx-auto px-6 py-20 md:py-24">
        <div className="max-w-2xl">
          <SectionEyebrow>{s.eyebrow}</SectionEyebrow>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight leading-tight">
            {s.title}
          </h2>
          <p className="mt-5 text-base text-muted-foreground leading-relaxed">
            {s.lead}
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
          {s.cards.map((card, i) => (
            <DifferenceCard key={i} card={card} testId={`difference-card-${i}`} />
          ))}
        </div>
      </div>
    </section>
  );
}

function DifferenceCard({ card, testId }) {
  const Icon = card.icon;
  return (
    <div
      data-testid={testId}
      className="relative rounded-xl border border-border/60 bg-card p-6 hover:border-primary/40 transition"
    >
      <div className="w-10 h-10 rounded-md bg-primary/10 text-primary grid place-items-center mb-4">
        <Icon size={18} weight="duotone" />
      </div>
      <div className="text-base font-bold tracking-tight">{card.title}</div>
      <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{card.desc}</p>
    </div>
  );
}

function ProofBand({ c }) {
  return (
    <section className="border-t border-border/60 bg-primary/[0.03]" data-testid="section-proof">
      <div className="max-w-6xl mx-auto px-6 py-16 md:py-20 grid md:grid-cols-2 gap-10 items-start">
        <div>
          <div className="acas-eyebrow mb-3">{c.eyebrow}</div>
          <h3 className="text-2xl sm:text-3xl font-bold tracking-tight">{c.proof.title}</h3>
          <p className="mt-4 text-muted-foreground leading-relaxed">{c.proof.lead}</p>
        </div>
        <ul className="space-y-3">
          {c.proof.bullets.map((b, i) => (
            <li key={i} className="flex items-start gap-3 text-sm" data-testid={`proof-bullet-${i}`}>
              <span className="w-5 h-5 rounded-full bg-primary/15 text-primary grid place-items-center flex-shrink-0 mt-0.5">
                <Check size={12} weight="bold" />
              </span>
              <span className="leading-relaxed">{b}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function FinalCTA({ c }) {
  return (
    <section className="border-t border-border/60" data-testid="section-final-cta">
      <div className="max-w-4xl mx-auto px-6 py-20 md:py-24 text-center">
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">{c.finalCta.title}</h2>
        <p className="mt-4 text-muted-foreground max-w-2xl mx-auto leading-relaxed">
          {c.finalCta.lead}
        </p>
        <div className="mt-8 flex flex-wrap gap-3 justify-center">
          <Link to="/kayit">
            <Button size="lg" className="gap-2 h-11 px-5 text-sm" data-testid="final-cta-primary">
              {c.finalCta.primary}
              <ArrowRight size={16} weight="bold" />
            </Button>
          </Link>
          <Link to="/giris">
            <Button variant="outline" size="lg" className="h-11 px-5 text-sm" data-testid="final-cta-secondary">
              {c.finalCta.secondary}
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}

function Footer({ c, locale }) {
  return (
    <footer className="border-t border-border/60" data-testid="home-footer">
      <div className="max-w-6xl mx-auto px-6 py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Wordmark size="xs" />
          <div className="text-xs text-muted-foreground">{c.footer.copyright}</div>
        </div>
        <div className="text-xs text-muted-foreground flex items-center gap-2">
          <Sparkle size={12} weight="fill" className="text-accent" />
          {locale === "en" ? "Powered by Gemini 3 Flash." : "Gemini 3 Flash ile desteklenmektedir."}
        </div>
      </div>
    </footer>
  );
}
